<script lang="ts">
	import Mill from '$lib/logos/mill.svelte';
	import Burger from '$lib/icons/burger.svelte';
	import type { SbImage, SbLink, StripeProductData } from '$lib/storyblok/types';
	import { fly } from 'svelte/transition';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import ShoppingCartIcon from '$lib/icons/shoppingCartIcon.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import BurgerMenu from '$lib/storyblok/components/burgerMenu.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { onMount } from 'svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import { device } from '$lib/state/device.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import Overlay from '$lib/storyblok/components/overlay.svelte';
	import log from '$lib/utils/log';

	interface Props {
		content: ISbStoryData<{
			center_navigation: Array<ISbStoryData['content']>;
			right_navigation: Array<ISbStoryData['content']>;
			cta_title: string;
			cta_link: SbLink;
			cta_bundle?: string;
			cta_stripe_product?: StripeProductData;
			burger_image: SbImage;
			alternative_logo?: SbImage;
			opens_overlay?: boolean;
			overlay_blocks: Array<ISbStoryData>;
			hide_cart_button?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let burgerMenu = $state({ open: () => {} });

	let visible = $state(false);

	onMount(() => {
		const heroElement = document.getElementById('hero');

		if (!heroElement) return;

		const observer = new IntersectionObserver(
			(entries) => {
				visible = !entries[0].isIntersecting;
			},
			{ threshold: 0 }
		);
		observer.observe(heroElement);
	});

	let ctaUrl = $derived(getSbUrl(content.cta_link));

	let overlay = $state<{ show: () => void }>();

	let ctaHasClickHandler = $derived(
		content.opens_overlay || (content.cta_bundle && content.cta_stripe_product)
	);

	function onClick() {
		if (content.opens_overlay) {
			overlay?.show();
			return;
		}

		if (!content.cta_stripe_product) {
			log.scope('FloatingNavigation').error('No stripe product found');
			return;
		}

		checkoutSession.select(content.cta_stripe_product, {
			productBundle: content.cta_bundle
		});
		shoppingCart.open();
	}
</script>

{#if visible}
	<div
		use:editable={content}
		transition:fly={{ y: '-100%', opacity: 1 }}
		class="fixed inset-x-0 top-0 z-50 flex items-center justify-between bg-white px-5 py-3 transition-transform lg:px-10 lg:py-5"
		style="transform: translate3d(0, var(--banner-height), 0)"
	>
		<a title="Home" class="flex flex-1" href="/">
			{#if content.alternative_logo?.filename}
				<Image
					src={content.alternative_logo.filename}
					alt={content.alternative_logo.alt || 'Logo'}
					class="h-8 lg:h-10"
					height={32}
				/>
			{:else}
				<Mill class="h-8 cursor-pointer text-green-3 lg:h-10" />
			{/if}
		</a>

		{#if content.center_navigation && content.center_navigation.length > 0}
			<nav class="flex-[2] max-lg:hidden">
				<ul class="flex items-center justify-center gap-x-2">
					{#each content.center_navigation as item}
						<Blok content={item} btnClasses="btn-white" />
					{/each}
				</ul>
			</nav>
		{/if}

		<div class="flex flex-1 items-center justify-end gap-x-2 lg:gap-x-[1px]">
			{#if content.right_navigation && content.right_navigation.length > 0}
				<nav class="max-lg:hidden">
					<ul class="flex items-center gap-x-2">
						{#each content.right_navigation as item}
							<Blok content={item} btnClasses="btn-white" />
						{/each}
					</ul>
				</nav>
			{/if}

			{#if !content.hide_cart_button}
				<Button
					class="btn-tertiary btn-white "
					title="Open cart"
					onclick={() => shoppingCart.open()}
				>
					<ShoppingCartIcon />
				</Button>
			{/if}

			<Button
				{...ctaHasClickHandler ? { onclick: onClick } : { href: ctaUrl }}
				class={['lg:ml-4', device.data.isDesktop ? null : 'btn-small']}
			>
				{content.cta_title}
			</Button>

			{#if content.right_navigation && content.right_navigation.length > 0}
				<nav class="lg:hidden">
					<Button class="btn-tertiary" onclick={() => burgerMenu.open()} title="Open burger menu">
						<Burger class="text-brown-1" style="width: 40px; height: 40px" />
					</Button>
				</nav>
			{/if}
		</div>
	</div>
{/if}

{#if content.center_navigation && content.center_navigation.length > 0}
	<BurgerMenu
		navList={content.center_navigation}
		signIn={content.right_navigation[0] || undefined}
		ctaLink={content.cta_link}
		ctaTitle={content.cta_title}
		ctaImage={content.burger_image}
		opens_overlay={content.opens_overlay}
		onoverlayopen={() => overlay?.show()}
		bind:this={burgerMenu}
	/>
{/if}

{#if content.opens_overlay}
	<Overlay padding={false} bind:this={overlay}>
		{#each content.overlay_blocks || [] as block}
			<Blok content={block} />
		{/each}
	</Overlay>
{/if}
